import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  Divider,
  Flex,
  Form,
  message,
  Modal,
  ModalProps
} from 'antd'
import { FormInstance } from 'antd/lib'

import {
  useTreatmentServiceCreateTreatmentPlanRecipeEndpoint,
  useTreatmentServiceGetTreatmentPlanRecipeListEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { SaveConfirm, Text } from 'src/components'
import {
  EXISTING_TREATMENT_PLAN,
  RECIPE_MEDICINE_FORM
} from 'src/features/persons/utils/constants'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { RecipesConsumablesTables } from './RecipesConsumablesTables'

type Props = {
  form?: FormInstance
  onCancel: () => void
} & ModalProps

const MODAL_FIELDS = ['dateIssued', RECIPE_MEDICINE_FORM]

export const AddRecipeModal = ({ form, ...props }: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const treatmentPlanId = form?.getFieldValue(EXISTING_TREATMENT_PLAN)

  const { mutate } = useTreatmentServiceCreateTreatmentPlanRecipeEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: async () => {
      message.success(t('consultations.recipe_added_successful'))
      queryClient.invalidateQueries({
        queryKey: [
          useTreatmentServiceGetTreatmentPlanRecipeListEndpointKey,
          String(treatmentPlanId)
        ]
      })
    }
  })

  const onFinish = async () => {
    const values = await form?.validateFields(MODAL_FIELDS)

    const newEntry = {
      dateIssued: values.dateIssued,
      medicationDispenses: values.recipesMedicine || []
    }

    mutate({
      id: String(treatmentPlanId),
      requestBody: {
        recipe: newEntry
      }
    })

    form?.resetFields(MODAL_FIELDS)
    props.onCancel()
  }

  return (
    <Modal
      open
      maskClosable={false}
      centered
      width={900}
      title={
        <Text size="xl" weight={700}>
          {t('consultations.add_new_recipe_title')}
        </Text>
      }
      footer={
        <Flex justify="center" gap={8}>
          <Button onClick={props.onCancel}>{t('common.cancel')}</Button>
          <SaveConfirm title={`${t('roles.confirm')}?`} onConfirm={onFinish}>
            <Button type="primary">{t('roles.confirm')}</Button>
          </SaveConfirm>
        </Flex>
      }
      {...props}
    >
      <Flex vertical gap={16}>
        <Form layout="vertical" form={form} requiredMark={false}>
          <Form.Item
            label={t('consultations.recipe_date')}
            name="dateIssued"
            rules={requiredRules}
          >
            <DatePicker
              className="full-width"
              placeholder={t('consultations.recipe_date')}
              format={dayMonthYearBackSlash}
            />
          </Form.Item>

          <Divider />
          <Form.Item name={RECIPE_MEDICINE_FORM} rules={requiredRules}>
            <Form.List name={RECIPE_MEDICINE_FORM}>
              {(fields, { remove }) => (
                <RecipesConsumablesTables
                  fields={fields}
                  remove={remove}
                  form={form}
                />
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Flex>
    </Modal>
  )
}
